import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore";

import { FaArrowLeft, FaSave, FaSpinner } from "react-icons/fa";

import { db } from "../../../firebase/firebase";

import "./EditProject.css";

const EditProject = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [project, setProject] = useState({
    title: "",
    slug: "",
    sport: "",
    surface: "",
    client: "",
    location: "",
    state: "",
    completionDate: "",
    courtLength: "",
    courtWidth: "",
    area: "",
    shortDescription: "",
    description: "",
    featured: false,
    seoTitle: "",
    seoDescription: "",
  });

  /* ================================
     LOAD PROJECT
  ================================= */

  useEffect(() => {
    const loadProject = async () => {
      try {
        setLoading(true);
        setError("");

        const projectRef = doc(db, "projects", id);
        const snapshot = await getDoc(projectRef);

        if (!snapshot.exists()) {
          throw new Error("Project not found.");
        }

        const data = snapshot.data();

        setProject({
          title: data.title || "",
          slug: data.slug || "",
          sport: data.sport || "",
          surface: data.surface || "",
          client: data.client || "",
          location: data.location || "",
          state: data.state || "",
          completionDate: data.completionDate || "",
          courtLength: data.courtLength || "",
          courtWidth: data.courtWidth || "",
          area: data.area || "",
          shortDescription: data.shortDescription || "",
          description: data.description || "",
          featured: data.featured || false,
          seoTitle: data.seoTitle || "",
          seoDescription: data.seoDescription || "",
        });
      } catch (err) {
        console.error("EDIT PROJECT LOAD ERROR:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadProject();
  }, [id]);

  /* ================================
     HANDLE CHANGE
  ================================= */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setProject((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  /* ================================
     SAVE
  ================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");

      const projectRef = doc(db, "projects", id);

      await updateDoc(projectRef, {
        ...project,
        updatedAt: serverTimestamp(),
      });

      alert("Project updated successfully.");

      navigate(`/admin/projects/${id}`);
    } catch (err) {
      console.error("UPDATE PROJECT ERROR:", err);

      setError(err.message || "Failed to update project.");
    } finally {
      setSaving(false);
    }
  };

  /* ================================
     LOADING
  ================================= */

  if (loading) {
    return (
      <div className="edit-project-page">
        <div className="edit-loading">
          <FaSpinner className="edit-spinner" />
          <h2>Loading Project...</h2>
          <p>Please wait.</p>
        </div>
      </div>
    );
  }

  /* ================================
     ERROR
  ================================= */

  if (error && !project.title) {
    return (
      <div className="edit-project-page">
        <button
          className="edit-back-button"
          onClick={() => navigate("/admin/projects")}
        >
          <FaArrowLeft />
          Back to Projects
        </button>

        <div className="edit-error">
          <h2>Unable to Load Project</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="edit-project-page">
      {/* HEADER */}

      <div className="edit-project-header">
        <button
          className="edit-back-button"
          onClick={() => navigate(`/admin/projects/${id}`)}
        >
          <FaArrowLeft />
          Back to Project
        </button>

        <div>
          <h1>Edit Project</h1>
          <p>Update your sports construction project information.</p>
        </div>
      </div>

      {/* ERROR */}

      {error && <div className="edit-form-error">{error}</div>}

      {/* FORM */}

      <form className="edit-project-form" onSubmit={handleSubmit}>
        {/* ================================
            PROJECT INFORMATION
        ================================= */}

        <div className="edit-card">
          <div className="edit-card-header">
            <h2>Project Information</h2>
            <p>Basic project information</p>
          </div>

          <div className="edit-grid">
            {/* TITLE */}

            <div className="edit-group full">
              <label>Project Title *</label>

              <input
                type="text"
                name="title"
                value={project.title}
                onChange={handleChange}
                required
              />
            </div>

            {/* SLUG */}

            <div className="edit-group">
              <label>Slug *</label>

              <input
                type="text"
                name="slug"
                value={project.slug}
                onChange={handleChange}
                required
              />
            </div>

            {/* SPORT */}

            <div className="edit-group">
              <label>Sport *</label>

              <select
                name="sport"
                value={project.sport}
                onChange={handleChange}
                required
              >
                <option value="">Select Sport</option>

                <option value="Basketball">Basketball</option>

                <option value="Tennis">Tennis</option>

                <option value="Badminton">Badminton</option>

                <option value="Football">Football</option>

                <option value="Volleyball">Volleyball</option>

                <option value="Running Track">Running Track</option>

                <option value="Pickleball">Pickleball</option>

                <option value="Squash">Squash</option>

                <option value="Cricket">Cricket</option>
              </select>
            </div>

            {/* SURFACE */}

            <div className="edit-group">
              <label>Surface *</label>

              <select
                name="surface"
                value={project.surface}
                onChange={handleChange}
                required
              >
                <option value="">Select Surface</option>

                <option value="Acrylic">Acrylic</option>

                <option value="PU">PU</option>

                <option value="Synthetic Turf">Synthetic Turf</option>

                <option value="Wooden">Wooden</option>

                <option value="EPDM">EPDM</option>
              </select>
            </div>

            {/* CLIENT */}

            <div className="edit-group">
              <label>Client Name</label>

              <input
                type="text"
                name="client"
                value={project.client}
                onChange={handleChange}
              />
            </div>

            {/* LOCATION */}

            <div className="edit-group">
              <label>City / Location</label>

              <input
                type="text"
                name="location"
                value={project.location}
                onChange={handleChange}
              />
            </div>

            {/* STATE */}

            <div className="edit-group">
              <label>State</label>

              <input
                type="text"
                name="state"
                value={project.state}
                onChange={handleChange}
              />
            </div>

            {/* DATE */}

            <div className="edit-group">
              <label>Completion Date</label>

              <input
                type="date"
                name="completionDate"
                value={project.completionDate}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* ================================
            DIMENSIONS
        ================================= */}

        <div className="edit-card">
          <div className="edit-card-header">
            <h2>Project Dimensions</h2>
            <p>Measurements of the project</p>
          </div>

          <div className="edit-grid">
            <div className="edit-group">
              <label>Length (ft)</label>

              <input
                type="number"
                name="courtLength"
                value={project.courtLength}
                onChange={handleChange}
              />
            </div>

            <div className="edit-group">
              <label>Width (ft)</label>

              <input
                type="number"
                name="courtWidth"
                value={project.courtWidth}
                onChange={handleChange}
              />
            </div>

            <div className="edit-group">
              <label>Total Area (sq.ft)</label>

              <input
                type="number"
                name="area"
                value={project.area}
                onChange={handleChange}
              />
            </div>
          </div>
        </div>

        {/* ================================
            DESCRIPTION
        ================================= */}

        <div className="edit-card">
          <div className="edit-card-header">
            <h2>Project Description</h2>
          </div>

          <div className="edit-group full">
            <label>Short Description</label>

            <textarea
              name="shortDescription"
              value={project.shortDescription}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="edit-group full">
            <label>Full Description</label>

            <textarea
              name="description"
              value={project.description}
              onChange={handleChange}
              rows="7"
            />
          </div>
        </div>

        {/* ================================
            SEO
        ================================= */}

        <div className="edit-card">
          <div className="edit-card-header">
            <h2>SEO Settings</h2>
            <p>Search engine optimization</p>
          </div>

          <div className="edit-group full">
            <label>SEO Title</label>

            <input
              type="text"
              name="seoTitle"
              value={project.seoTitle}
              onChange={handleChange}
            />
          </div>

          <div className="edit-group full">
            <label>SEO Description</label>

            <textarea
              name="seoDescription"
              value={project.seoDescription}
              onChange={handleChange}
              rows="5"
            />
          </div>
        </div>

        {/* ================================
            FEATURED
        ================================= */}

        <div className="edit-featured">
          <input
            type="checkbox"
            name="featured"
            checked={project.featured}
            onChange={handleChange}
          />

          <div>
            <strong>Featured Project</strong>

            <p>Display this project in the featured projects section.</p>
          </div>
        </div>

        {/* ================================
            ACTIONS
        ================================= */}

        <div className="edit-actions">
          <button
            type="button"
            className="edit-cancel"
            onClick={() => navigate(`/admin/projects/${id}`)}
          >
            Cancel
          </button>

          <button type="submit" className="edit-save" disabled={saving}>
            {saving ? (
              <>
                <FaSpinner className="edit-spinner-small" />
                Saving...
              </>
            ) : (
              <>
                <FaSave />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditProject;
